import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import MarketplaceCard from '../components/MarketplaceCard';
import { API_URL } from '../config';


const faqsMT4 = [
  {
    question: "Can I use these MT4 EAs on MetaTrader 5?",
    answer: "No, MT4 and MT5 use different programming languages (MQL4 vs MQL5). These specific files are compiled for MetaTrader 4 only. If you use MT5, please browse our MT5 category."
  },
  {
    question: "How do I install an EA on MetaTrader 4?",
    answer: "Open your MT4 terminal, go to File > Open Data Folder, navigate to MQL4 > Experts, and paste the downloaded .ex4 file there. Restart MT4, drag the EA onto your chart, and ensure \"Allow Live Trading\" is checked."
  },
  {
    question: "Do these MT4 EAs work on MT4 prop firm accounts?",
    answer: "Yes, most of our EAs are compatible with MT4 prop firm servers. Check the specific product description for prop-safety certifications."
  }
];

const faqsMT5 = [
  {
    question: "Is MetaTrader 5 better for running trading robots?",
    answer: "Yes, MT5 supports multi-threaded backtests and real tick historical data, allowing for much more accurate simulation and optimization than MT4."
  },
  {
    question: "Are these MT5 EAs fully unlocked?",
    answer: "Yes, all our MT5 EAs are fully unlocked binaries (.ex5) with no account restrictions or licensing limits. You can run them on unlimited demo and live accounts."
  },
  {
    question: "Do I need a special broker account for MT5 EAs?",
    answer: "You need a broker that supports MetaTrader 5. Most modern brokers (like IC Markets, Exness, Pepperstone) offer both MT4 and MT5 account types."
  }
];

const CategoryPage = () => {
  const { platform } = useParams(); // 'mt4' or 'mt5'
  const isMT5 = platform?.toLowerCase() === 'mt5';
  const platformName = isMT5 ? 'MT5' : 'MT4';
  const currentFaqs = isMT5 ? faqsMT5 : faqsMT4;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/products`)
      .then(res => res.json())
      .then(data => {
        // Filter by platform
        const filtered = data.filter(product => {
          const searchString = `${product.name} ${product.description} ${product.longDescription} ${product.features?.join(' ')} ${product.extendedFeatures?.join(' ')}`.toLowerCase();
          return searchString.includes(platformName.toLowerCase());
        });
        setProducts(filtered);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch products', err);
        setLoading(false);
      });
  }, [platformName]);

  return (
    <main className="min-h-screen relative pt-24 pb-24">
      {/* Ambient background glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] -z-10 pointer-events-none"></div>
      
      <section className="max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop">
        
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-display-lg text-display-lg text-silver-text mb-4">
            Premium <span className="text-primary">{platformName}</span> Expert Advisors
          </h1>
          <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl">
            Browse our curated collection of verified algorithmic trading systems built specifically for MetaTrader {isMT5 ? '5' : '4'}.
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-gutter-desktop mb-24">
          {loading ? (
            <div className="col-span-full flex flex-col items-center justify-center py-24">
              <div className="w-16 h-16 border-4 border-white/10 border-t-primary rounded-full animate-spin mb-6"></div>
              <div className="text-on-surface-variant font-bold text-lg animate-pulse tracking-wide">Loading {platformName} EAs...</div>
            </div>
          ) : (
            <>
              {products.map(product => (
                <MarketplaceCard key={product.id} product={product} />
              ))}
              {products.length === 0 && (
                <div className="col-span-full text-center py-12 text-on-surface-variant bg-surface-container-low rounded-xl border border-white/5">
                  <span className="material-symbols-outlined text-4xl mb-3 text-white/20">search_off</span>
                  <p className="font-bold text-silver-text">No {platformName} Expert Advisors found.</p>
                  <p className="text-sm mt-1">Check back soon for new additions to this category.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display-md text-[28px] font-bold text-silver-text mb-8 text-center md:text-left">
            Frequently Asked Questions ({platformName.toLowerCase()})
          </h2>

          <div className="space-y-4">
            {currentFaqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#1A1B20] border border-white/5 rounded-2xl overflow-hidden"
              >
                <div className="w-full px-6 py-5 flex items-start text-left">
                  <span className="text-primary font-bold mr-3 mt-0.5">Q:</span>
                  <span className="font-bold text-[15px] text-primary">
                    {faq.question}
                  </span>
                </div>
                <div className="px-6 pb-6 pt-0 text-[14px] text-on-surface-variant leading-relaxed pl-[42px]">
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>

      </section>
    </main>
  );
};

export default CategoryPage;
